import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ClaimsImageUploadService } from '../claims-image-upload-service';
import { FigureAddService, type FigureData } from '../figure-add-service';
import { BatchUploadFiguresApi, type BatchUploadFiguresUploadedFile, type UploadedFailedFile } from '../../apis/batch-upload-figures-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { Message } from '@patsnap/tahiti-core';
import { generateUUID } from '@telecom/shared/utils/uuid';

/**
 * 客户端实现：将文件转 base64 并添加到附图。
 */
@injectable()
export class ClaimsImageUploadClientService implements ClaimsImageUploadService {
    constructor(
        @inject(FigureAddService)
        private _figureAddService: FigureAddService,
    ) {}

    async addFiles(files: File[]): Promise<void> {
        const urls = await Promise.all(files.map(async (file) => {
            const url = await this._readFileAsBase64(file);
            return { url, s3Key: '', uniqueId: generateUUID() };
        }));
        this._figureAddService.addFigures(urls);
    }

    private _readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }
}

/**
 * 服务端实现：将文件上传到服务器，并添加到附图。
 */
@injectable()
export class ClaimsImageUploadServerService implements ClaimsImageUploadService {
    // eslint-disable-next-line max-params
    constructor(
        @inject(FigureAddService)
        private _figureAddService: FigureAddService,
        @inject(BatchUploadFiguresApi)
        private _batchUploadFiguresApi: BatchUploadFiguresApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(Message)
        private _message: Message,
    ) {}

    async addFiles(files: File[]): Promise<void> {
        const urls = await this._uploadFiles(files);
        this._addFigures(urls);
    }

    private _addFigures(urls: FigureData[]): void {
        this._figureAddService.addFigures(urls);
    }

    private async _uploadFiles(files: File[]): Promise<FigureData[]> {
        const form = this._createFormData(files);
        const data = await this._batchUploadFiguresApi.batchUploadFigures({
            taskId: this._currentTaskService.taskId,
            form,
        });

        this._handleFailedFiles(data.failed_files);
        return this._extractUploadedFileUrls(data.uploaded_files);
    }

    private _createFormData(files: File[]): FormData {
        const form = new FormData();
        for (const file of files) {
            form.append('files', file);
        }
        return form;
    }

    private _handleFailedFiles(failedFiles: UploadedFailedFile[]): void {
        if (!failedFiles || failedFiles.length === 0) {
            return;
        }

        const failedMessages = failedFiles.map(failedFile => {
            const sizeInMB = (failedFile.file_size / (1024 * 1024)).toFixed(2);
            return `${failedFile.original_name}（错误码：${failedFile.error_code}，大小：${sizeInMB}MB，错误信息：${failedFile.error_message}）`;
        });

        this._message.warning({
            message: `以下图片上传失败：${failedMessages.join('；')}`,
        });
    }

    private _extractUploadedFileUrls(uploadedFiles: BatchUploadFiguresUploadedFile[]): FigureData[] {
        return uploadedFiles.map((file) => ({
            url: file.file_url,
            s3Key: file.s3_key,
            uniqueId: file.unique_id,
        }));
    }
}
